import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UILink from 'UIComponents/link/UILink';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { MONTHLY } from 'self-service-api/constants/Terms';
import Screens from 'pricing-pages-ui/constants/routing/Screens';
import { APP } from 'pricing-pages-ui/constants/tracking/Locations';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { calculatedPriceSelector } from '../../selectors/PricingSelectors';
import { suiteProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { isEligibleForSummer2022RetailPromotionSelector } from '../../selectors/PromotionSelectors';
import { suitePurchaseMotionsMapSelector } from '../../selectors/PurchaseMotionSelectors';
import { upgradeProductSelector } from '../../selectors/ProductsSelectors';
import CodeSplitEnvWrapper from '../common/env/CodeSplitEnvWrapper';
import PriceDisplay from '../common/pricing/PriceDisplay';

const StarterSuiteRetailPromotionBanner = ({
  screen
}) => {
  const currencyCode = useSelector(activeCurrencySelector);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isEligible = useSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return null;
    return isEligibleForSummer2022RetailPromotionSelector(state);
  });
  const suiteProductConfigurations = useSelector(state => suiteProductConfigurationsSelector(state, {
    productLevel: STARTER
  }));
  const price = useSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return null;
    return calculatedPriceSelector(state, {
      productConfigurations: suiteProductConfigurations
    }).monthly.fullPrice;
  });
  const suitePurchaseMotions = useSelector(state => suitePurchaseMotionsMapSelector(state)[STARTER] || []);
  const upgradeProduct = useSelector(state => upgradeProductSelector(state, {
    skuIds: suiteProductConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const sourceId = 'starter-suite-promotion-banner';
  const upgradeData = {
    app: APP,
    screen: screen || Screens.SUITE,
    upgradeProduct,
    uniqueId: `${screen}-${sourceId}`
  };
  const termsAndConditionsURL = 'https://www.hubspot.com/starter-promotional-offer';

  const handleTermsAndConditionsLinkClick = () => {
    getUsageTracker().track('externalLinkClick', {
      name: 'terms-and-conditions',
      url: termsAndConditionsURL,
      trackingSource: sourceId
    });
  };

  if (!isEligible) return null;
  return /*#__PURE__*/_jsx(UIAlert, {
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "starterSuitePromotion.title"
    }),
    children: /*#__PURE__*/_jsxs(UIButtonWrapper, {
      children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: 'starterSuitePromotion.body_jsx',
        elements: {
          Price: PriceDisplay,
          UILink
        },
        options: {
          currencyCode,
          price,
          link: termsAndConditionsURL,
          onClick: handleTermsAndConditionsLinkClick
        }
      }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
        authedComponentLoader: () => import(
        /* webpackChunkName: "authed-chunk" */
        `pricing-pages-ui/components/common/purchaseMotions/PurchaseMotionButtonWrapper`),
        authedComponentProps: {
          use: 'tertiary',
          level: 1,
          productConfigurations: suiteProductConfigurations,
          purchaseMotions: suitePurchaseMotions,
          trackingSource: sourceId,
          queryParamTerm: MONTHLY,
          upgradeData,
          currencyCode,
          style: {
            padding: '5px 12px',
            fontWeight: 400,
            fontSize: '12px'
          }
        }
      })]
    })
  });
};

export default StarterSuiteRetailPromotionBanner;